import type { HostedPageData, PortalSession } from '@chargebee/chargebee-js-types/cb-types/interfaces/cb-types';
import { AxiosInstance } from 'axios';
import baseAxios, { fetchPaginatedDataFunction } from '@/helpers/forms/axios';
import { DataFetcherFunction } from '../../compiler/types';

export enum UsageType {
    OutboundShipmentItems = 'outbound_shipment_items',
}

export interface UsageMetricEvent {
    id: string
    event: string
    eventData: object
    occurredAt: string
    usageType: UsageType
    usageEffect: number
}

export interface UsageMetricOutboundShipmentItemEvent extends UsageMetricEvent {
    eventData: {
        itemId: number
        itemSku: string
        quantity: number
        outboundShipmentId: number
        outboundShipmentItemId: number
        // Absent or null on historical events the backend backfill could not resolve.
        merchantUuid?: string | null
    }
    usageType: UsageType.OutboundShipmentItems
}

export interface ServiceProviderBillingStatus {
    hasChargebeeAccount: boolean
    hasPaymentDueInvoices: boolean
    unpaidInvoicesTotal: {
        amount: number
        currency: string
    }
    hasTrialSubscription: boolean
    trialEndsAt: string | null
}

export default (axios: AxiosInstance = baseAxios): { fetchUsageMetricEvents: DataFetcherFunction<UsageMetricEvent>; checkStatus: () => Promise<ServiceProviderBillingStatus>; fetchPortal: () => Promise<PortalSession>; generateNewCheckout: (serviceProviderId: number) => Promise<HostedPageData>; } => ({
    fetchUsageMetricEvents: fetchPaginatedDataFunction<UsageMetricEvent>(
        '/api/service-provider-billing/usage-metric-events',
        undefined,
        axios,
    ),

    checkStatus: async (): Promise<ServiceProviderBillingStatus> => {
        const response = await axios.get<ServiceProviderBillingStatus>('/api/service-provider-billing/status');

        return response.data;
    },

    fetchPortal: async (): Promise<PortalSession> => {
        const response = await axios.get('/api/service-provider-billing/portal');

        return response.data.data?.portalSession;
    },

    generateNewCheckout: async (serviceProviderId: number): Promise<HostedPageData> => {
        const response = await axios.get('/api/service-provider-billing/hosted-page-new-checkout', {
            headers: {
                'X-Service-Provider-Id': serviceProviderId,
            },
        });

        return response.data.data;
    },
});
